import { FC, useMemo, useState } from 'react';
import { useStoreLocatorContext } from './context';
import {
  Alert,
  Button,
  Icon,
  IconSvg,
  Input,
  Spinner,
  TreezStore,
} from '@/components';
import classNames from 'classnames';

import { CURRENT_LOCATION_LABEL } from './constants';
import { StoreExternalItem } from './StoreLocator.components';
import styles from './storelocator.module.scss';
import { debounce } from 'lodash';
import { TreezAndExternalStore } from 'utils/utils';
interface IPickupSectionProps {
  showInstructionText: boolean;
  loadingLocation: boolean;
  handleUseCurrentLocation: () => void;
}

const PickupSection: FC<IPickupSectionProps> = ({
  showInstructionText,
  loadingLocation,
  handleUseCurrentLocation,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const {
    isPickup,
    isPickupInvalid,
    setIsPickupInvalid,
    singleStore,
    pickupSectionTitle,
    pickupAllStore,
    showSearchBox,
  } = useStoreLocatorContext();

  const handleChange = debounce(e => {
    setSearchTerm(e.target.value);
  }, 300);

  const filteredStores = useMemo(() => {
    const resultNameFilter = pickupAllStore?.filter(store =>
      store?.name?.toLowerCase().includes(searchTerm?.toLowerCase()),
    );
    return resultNameFilter || [];
  }, [pickupAllStore, searchTerm]);

  return (
    <div className={styles.pickup_section}>
      <Button
        ariaLabel={CURRENT_LOCATION_LABEL}
        className={classNames(styles.current_location, {
          [styles.hidden]: singleStore && isPickup,
        })}
        onClick={handleUseCurrentLocation}
      >
        {loadingLocation ? (
          <Spinner className={styles.crosshairs_icon} sm />
        ) : (
          <Icon className={styles.crosshairs_icon} name="crosshairs" />
        )}
        <span>{CURRENT_LOCATION_LABEL}</span>
      </Button>
      {showInstructionText && (
        <p className={styles.pickup_section__title}>{pickupSectionTitle}</p>
      )}
      {showSearchBox && (
        <div className={classNames(styles.store_selector__search_box)}>
          <Input
            autoComplete="off"
            className={styles.store_selector__search_box_input}
            isSmall
            name="pickup-searchBox"
            onChange={e => handleChange(e)}
            placeholder="Search"
            type="search"
          />
          <Button
            ariaLabel="Search"
            className={styles.store_selector__search_box_button}
            color="primary"
            onClick={() => {
              setSearchTerm(searchTerm);
            }}
          >
            <IconSvg name="search" />
          </Button>
        </div>
      )}

      <div className={styles.pickup_section__content}>
        {isPickup && isPickupInvalid && (
          <Alert
            error
            isOpen
            toggle={event => {
              event.stopPropagation();
              setIsPickupInvalid(false);
            }}
          >
            Please select a shop.
          </Alert>
        )}
        <StoresList
          filteredStores={filteredStores}
          loadingLocation={loadingLocation}
          searchTerm={searchTerm}
        />
      </div>
    </div>
  );
};

interface IStoresListProps {
  loadingLocation: boolean;
  filteredStores: TreezAndExternalStore[];
  searchTerm: string;
}

const StoresList: FC<IStoresListProps> = ({
  loadingLocation,
  filteredStores,
  searchTerm,
}) => {
  const { selectedStoreId, setSelectedStoreId } = useStoreLocatorContext();

  const emptyList = () => {
    return (
      <div className={styles.empty_locations} role="alert">
        <i
          className={classNames(
            'icon icon_search',
            styles.empty_locations__icon_search,
          )}
          role="presentation"
        />
        <span>No stores available</span>
        <p>
          No search results match
          <b>
            <em>&quot;{searchTerm}&quot;</em>
          </b>{' '}
          Try a different search
        </p>
      </div>
    );
  };

  return (
    <fieldset>
      {!filteredStores?.length && emptyList()}
      {filteredStores?.map(store => {
        const isActive = selectedStoreId === store.shortName;

        if (store?.externalStore) {
          return (
            <StoreExternalItem
              isOpen={true}
              isSelected={selectedStoreId === store?.shortName}
              key={store?.shortName}
              onSelected={setSelectedStoreId}
              store={store}
            />
          );
        }

        return (
          <TreezStore
            className={styles.store}
            distanceInMiles={store?.distance ?? ''}
            isActive={isActive}
            isLoadingLocation={loadingLocation}
            isRadioType
            key={store?.shortName}
            onSelected={setSelectedStoreId}
            store={store}
            withBorders={false}
          />
        );
      })}
    </fieldset>
  );
};

export { PickupSection };
